import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

export type TileGridLayout = {
  columns: number
  rows: number
  /** Per-tile pixel size that fits the container without overflow. */
  tileWidth: number
  tileHeight: number
}

type UseTileGridOptions = {
  /** Tile aspect ratio (width / height). Defaults to 16:9. */
  aspect?: number
  /** Gap between tiles in px. */
  gap?: number
  /**
   * Hard cap on the number of columns. Used by the responsive "Grid" view to
   * keep tiles tappable on narrow screens (e.g. 2 columns on mobile) instead of
   * shrinking everyone into a single wide row.
   */
  maxColumns?: number
}

/**
 * Compute the best rows×columns grid so {@link count} video tiles fit entirely
 * inside the measured container. Among layouts that fit, pick the one with the
 * largest tile area — mirrors Google Meet's "everyone visible" behavior.
 */
export function computeTileGrid(
  count: number,
  width: number,
  height: number,
  { aspect = 16 / 9, gap = 12, maxColumns }: UseTileGridOptions = {},
): TileGridLayout {
  if (count <= 0 || width <= 0 || height <= 0) {
    return { columns: 1, rows: 1, tileWidth: 0, tileHeight: 0 }
  }

  let best: TileGridLayout = { columns: 1, rows: count, tileWidth: 0, tileHeight: 0 }
  let bestRows = Infinity
  let bestArea = -1

  const columnCeiling = maxColumns && maxColumns > 0 ? Math.min(count, maxColumns) : count
  for (let columns = 1; columns <= columnCeiling; columns++) {
    const rows = Math.ceil(count / columns)
    // Width-constrained tile size.
    let tileWidth = (width - gap * (columns - 1)) / columns
    let tileHeight = tileWidth / aspect
    // If too tall to fit the rows, fall back to a height-constrained size.
    if (tileHeight * rows + gap * (rows - 1) > height) {
      tileHeight = (height - gap * (rows - 1)) / rows
      tileWidth = tileHeight * aspect
    }
    if (tileWidth <= 0 || tileHeight <= 0) continue

    const gridW = tileWidth * columns + gap * (columns - 1)
    const gridH = tileHeight * rows + gap * (rows - 1)
    if (gridW > width + 0.5 || gridH > height + 0.5) continue

    const area = tileWidth * tileHeight
    // Prefer fewer rows (wider grids) so everyone stays visible without a tall
    // vertical stack; tie-break by largest tile area.
    if (rows < bestRows || (rows === bestRows && area > bestArea)) {
      bestRows = rows
      bestArea = area
      best = { columns, rows, tileWidth, tileHeight }
    }
  }

  // If nothing fit (extreme aspect ratios), shrink to the height-constrained 1-col
  // layout so tiles still render instead of overflowing off-screen.
  if (bestArea < 0) {
    const rows = count
    const tileHeight = Math.max(0, (height - gap * (rows - 1)) / rows)
    const tileWidth = tileHeight * aspect
    return { columns: 1, rows, tileWidth, tileHeight }
  }

  return best
}

/** Sensible grid before the container is measured (avoids full-width stacked tiles). */
export function estimateTileGrid(count: number): TileGridLayout {
  if (count <= 0) return { columns: 1, rows: 1, tileWidth: 0, tileHeight: 0 }
  const columns = count === 1 ? 1 : count <= 4 ? 2 : count <= 9 ? 3 : 4
  const rows = Math.ceil(count / columns)
  const tileWidth = 160
  const tileHeight = tileWidth / (16 / 9)
  return { columns, rows, tileWidth, tileHeight }
}

export function useTileGrid(count: number, options?: UseTileGridOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => setSize({ width: el.clientWidth, height: el.clientHeight })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [count])

  useEffect(() => {
    const onResize = () => {
      const el = containerRef.current
      if (el) setSize({ width: el.clientWidth, height: el.clientHeight })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const layout = useMemo(() => {
    const measured = computeTileGrid(count, size.width, size.height, options)
    if (measured.tileWidth > 0 && measured.tileHeight > 0) return measured
    return estimateTileGrid(count)
  }, [count, size.width, size.height, options])

  return { containerRef, layout }
}
